import * as React from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useSnackbar } from 'notistack'
import { useSWRConfig } from 'swr'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { styled } from '@mui/material/styles'
import { Box, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import { StackProps } from '@mui/system'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useUser } from 'components/hooks/state'
import Button from 'components/atomic/atoms/button'
import Icon from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import Chip from 'components/atomic/atoms/chip'

import { ResourceOverview, ResourceProgress } from 'types/types'
import { mapIcon } from 'utils/resources'
import devvSdk from 'utils/devv-sdk'
import Routes from 'utils/routes'

interface WrapperProps extends StackProps {
    isLast: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Wrapper = styled(({ isLast, ...props }: WrapperProps) => <Stack {...props} />)`
    border-bottom: ${({ isLast, theme }) => (!isLast ? `1px solid ${theme.palette.divider}` : 'none')};
`

const StyledLink = styled(Text)`
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`

interface ResourceItemProps {
    rootId: string
    rootType: 'path' | 'course'
    resource: ResourceOverview
    resourceProgress?: ResourceProgress
    isLast?: boolean
}

const ResourceItem = React.memo((props: ResourceItemProps) => {
    const { rootId, rootType, resourceProgress, isLast = false } = props
    const { _id, title, description, type } = props.resource

    const [status, setStatus] = React.useState<'STARTED' | 'COMPLETED' | undefined>()
    const { t }: I18n = useTranslation()
    const { palette } = useTheme()
    const { enqueueSnackbar } = useSnackbar()
    const { mutate } = useSWRConfig()
    const { user } = useUser()
    const router = useRouter()

    React.useEffect(() => {
        setStatus(!!resourceProgress ? (!!resourceProgress.completed ? 'COMPLETED' : 'STARTED') : undefined)
    }, [resourceProgress])

    const isUserEnrolled = React.useMemo(() => {
        return rootType === 'path'
            ? user?.enrolled_paths?.includes(rootId) || user?.personalized_path === rootId
            : user?.enrolled_tracks?.includes(rootId) || user?.personalized_path === rootId
    }, [user, rootId, rootType])

    const handleDoneClick = () => {
        setStatus((currStatus) => {
            const newStatus = currStatus === 'COMPLETED' ? 'STARTED' : 'COMPLETED'

            devvSdk
                .setResourceCompletionStatus({ resourceId: _id, done: newStatus === 'COMPLETED' })
                .then(() => {
                    mutate(`${rootType}s/${rootId}/progress`)
                    if (newStatus === 'COMPLETED') {
                        enqueueSnackbar(t('resources:success.markAsDone'), { variant: 'success' })
                    }
                })
                .catch(() => {
                    // TODO: handle specific errors
                    enqueueSnackbar(t('common:errors.generic'), { variant: 'error' })
                })
            return newStatus
        })
    }

    const handleStartClick = () => {
        if (isUserEnrolled) {
            router.push(Routes.Resource(`${rootType}s`, rootId, type, _id))
        } else {
            enqueueSnackbar(t('path:errors.notEnrolled'), { variant: 'error' })
        }
    }

    return (
        <Wrapper pb={2} spacing={1} isLast={isLast}>
            <Stack direction="row" justifyContent="space-between">
                {/** Type and Title */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                        color="primary"
                        size="small"
                        variant="outlined"
                        icon={<Icon name={status === 'COMPLETED' ? 'taskAliIcon' : mapIcon(type)} />}
                        sx={{ border: 'none', background: palette.primary.light }}
                        label={t(`resources:types.${type}`)}
                        rounded={false}
                    />
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {isUserEnrolled ? (
                            <Link passHref href={Routes.Resource(`${rootType}s`, rootId, type, _id)}>
                                <StyledLink fontWeight={600}>{title}</StyledLink>
                            </Link>
                        ) : (
                            <Text fontWeight={600}>{title}</Text>
                        )}
                    </Box>
                </Stack>

                {/** CTA */}
                <Stack direction="row" spacing={1}>
                    {status !== 'COMPLETED' && (
                        <div>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleStartClick()}
                                endIcon={isUserEnrolled ? <PlayCircleIcon /> : <LockOutlinedIcon />}>
                                {status === 'STARTED'
                                    ? t('path:buttons.continueResource')
                                    : t('path:buttons.startResource')}
                            </Button>
                        </div>
                    )}

                    {isUserEnrolled && (
                        <Button
                            onClick={handleDoneClick}
                            color="inherit"
                            variant="outlined"
                            size="small"
                            endIcon={status === 'COMPLETED' ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                            sx={{ color: palette.grey[500] }}>
                            <Text>
                                {status === 'COMPLETED'
                                    ? t('path:resourceStatus.done')
                                    : t('path:resourceStatus.flagAsDone')}
                            </Text>
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {isUserEnrolled ? (
                    <Link passHref href={Routes.Resource(`${rootType}s`, rootId, type, _id)}>
                        <StyledLink fontWeight={600}>{title}</StyledLink>
                    </Link>
                ) : (
                    <Text fontWeight={600}>{title}</Text>
                )}
            </Box>

            {/** Description */}
            {description && ( // this helps remove the extra spacing when there is no description
                <Text variant="subtitle2">{description}</Text>
            )}
        </Wrapper>
    )
})

export default ResourceItem
